import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, QueryList, ViewChildren, Input} from '@angular/core';
import {MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell, MatHeaderRowDef, MatHeaderRow,
  MatRowDef, MatRow} from '@angular/material/table';
import {MatIcon} from '@angular/material/icon';
import {RouterLink} from '@angular/router';
import {NgClass, DatePipe} from '@angular/common';
import {Platform} from '@angular/cdk/platform';
import {TranslatePipe} from '@ngx-translate/core';
import {FocusKeyManager} from '@angular/cdk/a11y';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {FocusableTableItemDirective} from '../../shared/directive/focusable-table-item.directive';
import {DeliveryDescription} from '../../shared/model/delivery-description';
import {DeliveryType} from '../../shared/model/delivery-type';
import {NumberProductsPipe} from '../../shared/pipes/number-products.pipe';

@Component({
  selector: 'myupdates-delivery-items-table-view',
  templateUrl: './delivery-items-table-view.component.html',
  styleUrls: ['./delivery-items-table-view.component.scss'],
  imports: [MatTable, MatColumnDef, MatHeaderCellDef, MatHeaderCell, MatCellDef, MatCell,
    RouterLink, MatHeaderRowDef, MatHeaderRow, MatRowDef, MatRow, NgClass, MatIcon, TranslatePipe,
    DatePipe, NumberProductsPipe, FocusableTableItemDirective]
})
export class DeliveryItemsTableViewComponent implements AfterViewInit, OnDestroy {
  @Input() deliveries: DeliveryDescription[] = [];
  @Input() categoryName: string = '';
  @Input() breakPointExtension: string = '';
  @Input() selectedDelivery: DeliveryDescription | null = null;

  @ViewChildren(FocusableTableItemDirective) focusableLinks!: QueryList<FocusableTableItemDirective>;

  displayedColumns: string[] = ['deliveryDate', 'deliveryDescription', 'totalProducts', 'deliveryLink'];

  protected readonly DeliveryType = DeliveryType;

  private keyManager!: FocusKeyManager<FocusableTableItemDirective>;
  private destroy$ = new Subject<void>();

  constructor(public platform: Platform, private cdr: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.initKeyManager();

    this.focusableLinks.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.initKeyManager());
  }

  ngOnDestroy(): void {
    this.keyManager?.destroy();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initKeyManager(): void {
    this.keyManager?.destroy();

    if (!this.focusableLinks.length) return;

    const items = this.focusableLinks.toArray();

    this.keyManager = new FocusKeyManager(items)
      .withVerticalOrientation()
      .withHomeAndEnd()
      .withWrap();

    this.keyManager.change
      .pipe(takeUntil(this.destroy$))
      .subscribe((activeIndex: number) => {
        items.forEach((item, idx) => item.setActive(idx === activeIndex));
        this.cdr.detectChanges();
      });

    items.forEach((link, index) => {
      link.linkFocused
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.keyManager.setActiveItem(index);
        });
    });

    // Setze das erste Item als aktiv, aber erst nach einem Mikrotask
    // So wird verhindert, dass während der Initialisierung fokussiert wird und ein NG0100 kommt
    Promise.resolve().then(() => {
      if (this.keyManager && items.length > 0) {
        this.keyManager.updateActiveItem(0);
        items[0].setActive(true);
      }
    });
  }

  onLinkKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      if (event.currentTarget instanceof HTMLElement) {
        event.preventDefault();
        event.currentTarget.click();
      }
    }
  }

  onTableKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Tab') return;

    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault();
      this.keyManager?.onKeydown(event);
      this.keyManager?.activeItem?.focus();
    }
  }
}

